import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  MagnifyingGlass, Camera, Link as LinkIcon, Upload, ImageSquare,
  ArrowSquareOut, WarningCircle, BookmarkSimple,
} from "@phosphor-icons/react";
import SetThumb from "@/components/opportunities/SetThumb";

const money = (n) => (n == null ? "--" : `$${Number(n).toFixed(2)}`);

export default function Identify() {
  const [mode, setMode] = useState("sets");
  const [results, setResults] = useState(null);
  const [imageUrl, setImageUrl] = useState("");
  const [previewSrc, setPreviewSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState(null);
  const dropRef = useRef(null);
  const fileInputRef = useRef(null);

  const reset = () => { setResults(null); setNotice(null); setPreviewSrc(null); };

  const onFile = async (file) => {
    if (!file || !file.type.startsWith("image/")) {
      toast.error("Please drop an image file");
      return;
    }
    reset();
    setPreviewSrc(URL.createObjectURL(file));
    setLoading(true);
    try {
      const d = await api.identifyImage(file, mode, 5);
      setResults(d.predictions || []);
      if (!d.predictions?.length) toast.warning("No matches found — try a clearer photo");
      else toast.success(`Identified ${d.predictions.length} candidates`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Identification failed");
    } finally { setLoading(false); }
  };

  const onUrl = async () => {
    if (!imageUrl.trim()) return;
    reset();
    setPreviewSrc(imageUrl.trim());
    setLoading(true);
    try {
      const d = await api.identifyUrl(imageUrl.trim(), mode, 5);
      if (d.error) {
        setNotice({ kind: d.error, message: d.message });
        setResults([]);
      } else {
        setResults(d.predictions || []);
        if (!d.predictions?.length) toast.warning("No matches found");
        else toast.success(`Identified ${d.predictions.length} candidates`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lookup failed");
    } finally { setLoading(false); }
  };

  const onDrop = (e) => {
    e.preventDefault();
    dropRef.current?.classList.remove("border-[#E3000B]");
    const f = e.dataTransfer.files?.[0];
    if (f) onFile(f);
  };

  return (
    <div className="p-6 space-y-6 max-w-[1400px]">
      <div className="flex items-center gap-3">
        <Camera size={22} weight="bold" className="text-[#E3000B]" />
        <h1 className="font-head text-2xl font-black uppercase tracking-tighter" data-testid="identify-title">Identify by Image</h1>
        <span className="font-mono text-[10px] text-gray-500 ml-2">powered by Brickognize</span>
      </div>

      {/* Mode picker */}
      <div className="flex items-center gap-2">
        <span className="font-mono text-[10px] uppercase tracking-wider text-gray-500">SEARCH MODE</span>
        {["sets", "parts", "figs", "all"].map(m => (
          <button key={m} onClick={() => setMode(m)}
            data-testid={`identify-mode-${m}`}
            className={`px-3 h-7 rounded-sm border font-mono text-xs uppercase tracking-wider ${
              mode === m
                ? "border-[#E3000B] bg-[#E3000B]/10 text-[#E3000B]"
                : "border-[#E4E4E0] text-gray-600 hover:border-zinc-500"
            }`}>{m}</button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upload card */}
        <div className="border border-[#E4E4E0] rounded-sm">
          <div className="px-4 py-3 border-b border-[#E4E4E0] font-head font-bold uppercase text-xs tracking-wider text-gray-600 flex items-center gap-2">
            <Upload size={12} weight="bold" /> Drag & Drop Photo
          </div>
          <div
            ref={dropRef}
            onDragOver={e => { e.preventDefault(); dropRef.current?.classList.add("border-[#E3000B]"); }}
            onDragLeave={() => dropRef.current?.classList.remove("border-[#E3000B]")}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
            className="m-4 h-44 border-2 border-dashed border-[#E4E4E0] rounded-sm flex flex-col items-center justify-center cursor-pointer hover:border-[#E3000B] transition-colors"
            data-testid="identify-drop-zone"
          >
            <ImageSquare size={36} className="text-gray-500 mb-2" weight="duotone" />
            <span className="font-mono text-xs uppercase tracking-wider text-gray-600">
              {loading ? "Identifying…" : "Click or drop an image"}
            </span>
            <span className="text-[10px] font-mono text-gray-400 mt-1">JPEG / PNG, up to 10MB</span>
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden"
              onChange={e => e.target.files?.[0] && onFile(e.target.files[0])}
              data-testid="identify-file-input" />
          </div>
        </div>

        {/* URL paste card */}
        <div className="border border-[#E4E4E0] rounded-sm">
          <div className="px-4 py-3 border-b border-[#E4E4E0] font-head font-bold uppercase text-xs tracking-wider text-gray-600 flex items-center gap-2">
            <LinkIcon size={12} weight="bold" /> Paste Image URL
          </div>
          <div className="p-4 space-y-3">
            <Input
              value={imageUrl}
              onChange={e => setImageUrl(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") onUrl(); }}
              placeholder="https://… (direct image link)"
              className="h-9 bg-[#F6F6F3] border-[#E4E4E0] font-mono text-xs"
              data-testid="identify-url-input"
            />
            <Button onClick={onUrl} disabled={loading || !imageUrl.trim()}
              className="w-full h-9 bg-[#E3000B] text-white font-bold uppercase tracking-wider hover:bg-[#E6B800] disabled:opacity-50 rounded-sm"
              data-testid="identify-url-submit">
              <MagnifyingGlass size={12} weight="bold" className="mr-1" />
              IDENTIFY FROM URL
            </Button>
            <div className="text-[10px] font-mono text-gray-500 leading-relaxed pt-2 border-t border-[#E4E4E0]">
              <span className="text-[#E3000B] font-bold">FB Marketplace:</span> we'll attempt to pull the
              listing's preview image. Public listings usually work; region-locked or login-required
              ones fail — in that case, screenshot the photo and drag-drop it on the left.
            </div>
          </div>
        </div>
      </div>

      {/* Preview + results */}
      {(previewSrc || results || notice) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="identify-results">
          {previewSrc && (
            <div className="border border-[#E4E4E0] rounded-sm p-3">
              <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2">Input Image</div>
              <img src={previewSrc} alt="Preview" className="w-full max-h-72 object-contain bg-black" />
            </div>
          )}

          <div className="lg:col-span-2 space-y-3">
            {notice && (
              <div className="border border-[#FF3333]/40 bg-[#FF3333]/[0.06] rounded-sm p-4 flex items-start gap-3" data-testid="identify-notice">
                <WarningCircle size={20} weight="fill" className="text-[#FF3333] flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-head text-sm font-bold uppercase tracking-wider text-[#FF3333]">
                    {notice.kind === "marketplace_blocked" ? "Marketplace Blocked" : "Image Fetch Failed"}
                  </div>
                  <div className="text-xs font-mono text-gray-600 mt-1 leading-relaxed">{notice.message}</div>
                </div>
              </div>
            )}

            {results && results.length === 0 && !notice && !loading && (
              <div className="text-center font-mono text-sm text-gray-500 py-8" data-testid="identify-empty">
                No matches found. Try a clearer, more centered photo, or switch the search mode above.
              </div>
            )}

            {results && results.length > 0 && results.map(r => (
              <ResultCard key={r.id} r={r} />
            ))}

            {loading && !results && (
              <div className="text-center font-mono text-sm text-gray-500 py-8">Identifying…</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ResultCard({ r }) {
  const confidence = Math.round((r.score || 0) * 100);
  const tone = confidence >= 70 ? "text-[#00A850]" : confidence >= 40 ? "text-[#E3000B]" : "text-[#FF8C00]";
  return (
    <div className="border border-[#E4E4E0] rounded-sm p-4 flex items-center gap-4 hover:border-[#E3000B] transition-colors" data-testid={`identify-result-${r.id}`}>
      <SetThumb src={r.img_url} alt={r.name} size={56} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-head text-sm font-bold uppercase tracking-tight text-gray-900 truncate">{r.name || r.id}</span>
          <span className="font-mono text-[10px] text-[#E3000B]">{r.id}</span>
          {r.type && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-gray-200 text-gray-600">{r.type}</span>}
        </div>
        <div className="flex items-center gap-4 mt-1 font-mono text-[11px] text-gray-500 flex-wrap">
          <span>Confidence: <span className={`${tone} font-bold`}>{confidence}%</span></span>
          {r.theme && <span>Theme: <span className="text-gray-800">{r.theme}</span></span>}
          {r.year && <span>Year: <span className="text-gray-800">{r.year}</span></span>}
          {r.pieces && <span>{r.pieces} pcs</span>}
          {r.amazon_buy_box && (
            <span>AMZ: <span className="text-[#D97706] font-bold">{money(r.amazon_buy_box)}</span></span>
          )}
          {r.bricklink_lowest_us && (
            <span>BL: <span className="text-[#0055BF] font-bold">{money(r.bricklink_lowest_us)}</span></span>
          )}
          {r.roi_pct != null && (
            <span>ROI: <span className={`font-bold ${r.roi_pct >= 0 ? "text-[#00A850]" : "text-[#FF3333]"}`}>
              {r.roi_pct >= 0 ? "+" : ""}{r.roi_pct.toFixed(1)}%
            </span></span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {r.in_catalog && r.set_number && (
          <Link to={`/set/${r.set_number}`}
            data-testid={`identify-open-${r.set_number}`}
            className="px-3 h-8 rounded-sm bg-[#E3000B] text-white font-bold uppercase tracking-wider text-xs hover:bg-[#E6B800] flex items-center gap-1">
            <BookmarkSimple size={11} weight="bold" /> OPEN
          </Link>
        )}
        {!r.in_catalog && r.external_sites?.length > 0 && (
          <a href={r.external_sites[0].url} target="_blank" rel="noopener noreferrer"
            className="px-3 h-8 rounded-sm border border-[#E4E4E0] hover:border-[#E3000B] text-xs font-mono uppercase flex items-center gap-1">
            VIEW <ArrowSquareOut size={11} />
          </a>
        )}
      </div>
    </div>
  );
}
